import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { toConversationError } from './errors';
import { conversationKeys } from './keys';
import type { ReferencedInquiry } from './referencedInquiry';
import type { SharedSurvey } from './sharedSurvey';

import { ALWAYS_REFETCH } from '@/constants/time';
import type { components, paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type ThreadMessage = components['schemas']['ConversationMessage'];

export type ThreadSnapshot = components['schemas']['ConversationSnapshot'];

export type ThreadMeta = Omit<ThreadSnapshot, 'messages' | 'hasMore'>;

/** A file a stored message carries. Its `s3Key` is exchanged for a download URL. */
export type StoredMessageFile = components['schemas']['ConversationMessageFile'];

type SendMessageBody = components['schemas']['ConversationSendMessageRequest'];

/** A file as the send names it, once its bytes are uploaded. */
export type SentMessageFile = Omit<NonNullable<SendMessageBody['files']>[number], 'noPHI'>;

export type AttachmentMimeType = SentMessageFile['mimeType'];

/** Where a file was picked: the device camera, the photo library, or the file browser. */
export type AttachmentSource = NonNullable<SentMessageFile['source']>;

/**
 * A file picked on this device for a message. `uri` previews it before it is
 * sent. `s3Key` is set once its bytes are uploaded, so a retry after a failed
 * send reuses the upload rather than repeating it.
 */
export type OutgoingFile = {
  /** Stable while the file is picked, for list keys and removal. */
  id: string;
  name: string;
  mimeType: AttachmentMimeType;
  uri: string;
  size: number;
  source: AttachmentSource;
  /** The browser's File on web, where the bytes are read from it rather than the uri. */
  file?: File;
  s3Key?: string;
};

/**
 * A message this device is sending or failed to send. Its id is the id it is
 * sent under, which the server keeps as the stored message's id, so the two
 * copies can never both render.
 */
export type LocalMessage = {
  id: string;
  body: string;
  createdAt: string;
  status: 'pending' | 'failed';
  reference?: MessageReference;
  /** The survey a message shares, as the picker showed it, so a pending message shows its card. */
  survey?: SharedSurvey;
  files?: OutgoingFile[];
  /** Why the server refused the message, when it named a reason staff can act on. */
  failureCode?: string;
};

/**
 * An inquiry a message links to. The API takes only `inquiryId`; the
 * conversation id lets a pending message show the link before the stored copy,
 * which the server fills with the same conversation id, replaces it.
 */
export type MessageReference = {
  inquiryId: number;
  conversationId: string;
  /** What the picker showed for it, so a pending message shows the same card it will once stored. */
  details?: ReferencedInquiry;
};

export type SupportThread = {
  meta: ThreadMeta;
  messages: ThreadMessage[];
  local: LocalMessage[];
};

type SendMessageResult =
  paths['/v2/conversations/{conversationId}/messages/{messageId}']['put']['responses']['201']['content']['application/json']['data'];

type ResolveThreadResult =
  paths['/v2/support/threads/{userId}']['put']['responses']['200']['content']['application/json']['data'];

const PAGE_LIMIT = 200;

/** The index a read continues after: everything at or below it is held. */
export function lastStoredIndex(thread: SupportThread | undefined): number {
  return thread?.messages.at(-1)?.index ?? -1;
}

/**
 * Combines what is cached with what was just read. Stored messages are unioned
 * by id and kept in index order, because a send's own result can land in the
 * cache while a read is in flight. A local message is dropped once a stored
 * message carries its id.
 */
export function mergeThread(
  current: SupportThread | undefined,
  meta: ThreadMeta,
  fetched: readonly ThreadMessage[]
): SupportThread {
  const byId = new Map<string, ThreadMessage>();

  for (const message of [...(current?.messages ?? []), ...fetched]) {
    byId.set(message.id, message);
  }

  const messages = [...byId.values()].sort((left, right) => left.index - right.index);

  const local = (current?.local ?? []).filter((message) => !byId.has(message.id));

  return { meta, messages, local };
}

async function readSnapshot(conversationId: string, after: number): Promise<ThreadSnapshot> {
  try {
    const { data } = await fetchClient.GET('/v2/conversations/{conversationId}', {
      params: { path: { conversationId }, query: { after, limit: PAGE_LIMIT } },
    });

    if (!data) {
      throw new TypeError('Empty conversation response');
    }

    return data.data;
  } catch (error) {
    throw toConversationError(error);
  }
}

/**
 * Reads what the cache does not hold yet: the whole thread on first load,
 * then only messages after the last stored index. The API pages forward, so a
 * long gap is followed page by page until `hasMore` is false.
 */
export async function loadSupportThread(
  queryClient: QueryClient,
  conversationId: string
): Promise<SupportThread> {
  const key = conversationKeys.thread(conversationId);

  let after = lastStoredIndex(queryClient.getQueryData<SupportThread>(key));

  const fetched: ThreadMessage[] = [];

  let snapshot: ThreadSnapshot;

  do {
    snapshot = await readSnapshot(conversationId, after);

    fetched.push(...snapshot.messages);

    after = snapshot.messages.at(-1)?.index ?? after;
  } while (snapshot.hasMore && snapshot.messages.length > 0);

  const { messages: _messages, hasMore: _hasMore, ...meta } = snapshot;

  return mergeThread(queryClient.getQueryData<SupportThread>(key), meta, fetched);
}

/**
 * A support thread's messages, kept current incrementally. The read is cheap,
 * so every focus refetches it; a not-found thread is not retried, since the
 * caller is not a participant or the thread does not exist.
 */
export function getSupportThreadQueryOptions(queryClient: QueryClient, conversationId: string) {
  return queryOptions({
    queryKey: conversationKeys.thread(conversationId),
    queryFn: () => loadSupportThread(queryClient, conversationId),
    staleTime: ALWAYS_REFETCH,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) =>
      toConversationError(error).kind !== 'not_found' && failureCount < 2,
  });
}

/**
 * Sends one message under a client-generated id. Sending the same id again
 * returns the stored message rather than posting twice, so a retry after an
 * unknown outcome is always safe.
 *
 * Files must already be uploaded. Sending one is the author's confirmation
 * that it carries no PHI, which the composer asks for before it allows a send.
 */
export async function putSupportMessage(
  conversationId: string,
  messageId: string,
  body: string,
  referenceInquiryId?: number,
  files: readonly SentMessageFile[] = [],
  surveyGroupId?: string
): Promise<SendMessageResult> {
  const request: SendMessageBody = { body };

  if (referenceInquiryId !== undefined) {
    request.referenceInquiryId = referenceInquiryId;
  }

  if (surveyGroupId !== undefined) {
    request.surveyGroupId = surveyGroupId;
  }

  if (files.length > 0) {
    request.files = files.map((file) => ({ ...file, noPHI: true }));
  }

  try {
    const { data } = await fetchClient.PUT(
      '/v2/conversations/{conversationId}/messages/{messageId}',
      {
        params: { path: { conversationId, messageId } },
        body: request,
      }
    );

    if (!data) {
      throw new TypeError('Empty send response');
    }

    return data.data;
  } catch (error) {
    throw toConversationError(error);
  }
}

/** Marks read up to an index. The server never moves read state backwards. */
export async function markSupportThreadRead(
  conversationId: string,
  index: number
): Promise<number | null> {
  try {
    const { data } = await fetchClient.POST('/v2/conversations/{conversationId}/read', {
      params: { path: { conversationId } },
      body: { index },
    });

    return data?.data.lastReadIndex ?? null;
  } catch (error) {
    throw toConversationError(error);
  }
}

/**
 * The member's support thread, created empty if they have none. For staff
 * acting as Primum Support, to open a member's history before writing. Safe to
 * call for a thread nobody then writes in: an empty thread is listed in no
 * inbox, and the same member resolves to the same thread every time.
 */
export async function resolveSupportThread(userId: number): Promise<ResolveThreadResult> {
  try {
    const { data } = await fetchClient.PUT('/v2/support/threads/{userId}', {
      params: { path: { userId } },
    });

    if (!data) {
      throw new TypeError('Empty resolve response');
    }

    return data.data;
  } catch (error) {
    throw toConversationError(error);
  }
}
